// src/components/Modal/Modal.js
import React, { useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import styles from './Modal.module.css';

const Modal = ({ 
  isOpen, 
  onClose, 
  children, 
  title,
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscapeKey = true,
  maxWidth = '500px',
  maxHeight = '80vh',
  className = '',
  overlayClassName = '',
  ...props 
}) => {
  // Handle escape key press
  const handleEscapeKey = useCallback((event) => {
    if (closeOnEscapeKey && event.key === 'Escape') {
      onClose();
    }
  }, [closeOnEscapeKey, onClose]);

  // Handle overlay click
  const handleOverlayClick = useCallback((event) => {
    if (closeOnOverlayClick && event.target === event.currentTarget) {
      onClose();
    }
  }, [closeOnOverlayClick, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      
      if (closeOnEscapeKey) {
        document.addEventListener('keydown', handleEscapeKey);
      }
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
      if (closeOnEscapeKey) {
        document.removeEventListener('keydown', handleEscapeKey);
      }
    };
  }, [isOpen, closeOnEscapeKey, handleEscapeKey]);

  // Don't render if not open
  if (!isOpen) return null;

  const modalContent = (
    <div 
      className={`${styles.overlay} ${overlayClassName}`}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? "modal-title" : undefined}
    >
      <div 
        className={`${styles.content} ${className}`}
        style={{ 
          maxWidth, 
          maxHeight,
          ...props.style 
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        {(title || showCloseButton) && (
          <div className={styles.header}>
            {title && (
              <h2 id="modal-title" className={styles.title}>
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button 
                className={styles.closeButton}
                onClick={onClose}
                aria-label="Close modal"
                type="button"
              >
                <svg 
                  width="24" 
                  height="24" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Modal Body */}
        <div className={styles.body}>
          {children}
        </div>
      </div>
    </div>
  );

  // Render modal in portal (at document.body level)
  return ReactDOM.createPortal(modalContent, document.body);
};

export default Modal;